// The bulk fixes of the Products step, computed from the lines the store
// holds and the problems the last check reported on them (bos-app#213,
// US-03..US-06). bos-api only detects the problems; the defaults are the
// client's. Pure: the reducer applies them, the steps read them.
import type { CatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportLineResult,
    CatalogImportProblem,
} from '@/api/models';
import { gelNumberOf, tetriFromDecimal } from '@/utils/money';
import type {
    CatalogImportState,
    ParsedLine,
    ParsedLinePatch,
    ParsedNumberField,
} from './catalogImportReducer';

/** The problems that have a default fix; every other problem has none. */
export type BulkFixCode = Extract<
    CatalogImportCode,
    | 'BARCODE_LOOKS_LIKE_IMEI'
    | 'VARIANT_FIELDS_DIFFER'
    | 'GROUP_FIELDS_DIFFER'
    | 'BARCODE_SHARED_ACROSS_SKUS'
>;

/** The fixes in the order "Import with suggested fixes" applies them. */
const BULK_FIX_CODES: readonly BulkFixCode[] = [
    'BARCODE_LOOKS_LIKE_IMEI',
    'VARIANT_FIELDS_DIFFER',
    'GROUP_FIELDS_DIFFER',
    'BARCODE_SHARED_ACROSS_SKUS',
];

export const isBulkFixCode = (code: string): code is BulkFixCode =>
    BULK_FIX_CODES.includes(code as BulkFixCode);

/** The figures `VARIANT_FIELDS_DIFFER` compares across a variant's rows. */
export const VARIANT_MISMATCH_FIELDS = [
    'salePrice',
    'wholesalePrice',
    'minimumQuantity',
] as const satisfies readonly ParsedNumberField[];

export type VariantMismatchField = (typeof VARIANT_MISMATCH_FIELDS)[number];

/**
 * The product fields `GROUP_FIELDS_DIFFER` compares across a SKU's rows,
 * with the cells of the line each one is: an entity's id and its text go
 * together, so the rows agree however the server resolves them.
 */
export const GROUP_FIELD_CELLS = {
    name: ['name'],
    category: ['categoryId', 'categoryText'],
    brand: ['brandId', 'brandText'],
    unit: ['unit'],
    vatType: ['vatType'],
} as const satisfies Record<string, readonly (keyof ParsedLine)[]>;

export type GroupFixField = keyof typeof GROUP_FIELD_CELLS;

/** One value of a differing figure, with the rows that carry it. */
export type BulkFixValue = {
    /** `null` is a blank cell. */
    value: number | null;
    rows: number[];
};

/**
 * One differing figure of a variant: its values, the most common number
 * first (the higher on a tie) and a blank last, and the one the default
 * writes (`null` when no line has a number).
 */
export type BulkFixChoice = {
    field: VariantMismatchField;
    values: BulkFixValue[];
    suggested: number | null;
};

/** One group a fix applies to: a row, a variant, a SKU or a shared barcode. */
export type BulkFixGroup = {
    code: BulkFixCode;
    /** Tells the groups of one kind apart. */
    key: string;
    /** The group's rows the store holds and the user has not excluded, in row order. */
    rows: number[];
    /** What the default writes, by row: only the rows it changes. */
    patches: ReadonlyMap<number, ParsedLinePatch>;
    /** `VARIANT_FIELDS_DIFFER`: each differing figure's values; `[]` otherwise. */
    choices: BulkFixChoice[];
};

/** What the fixes read: the lines, the last check's answer and the exclusions. */
export type BulkFixSource = Pick<
    CatalogImportState,
    'lines' | 'preview' | 'exclusions'
>;

const blank = (value: string | null | undefined): boolean =>
    (value ?? '').trim() === '';

/** A code as catalog import matching compares it: trimmed, case-insensitive. */
const codeKey = (value: string | null | undefined): string =>
    (value ?? '').trim().toLowerCase();

const numberRows = (value: unknown): number[] =>
    Array.isArray(value)
        ? value.filter((row): row is number => Number.isInteger(row))
        : [];

const listedFields = (problem: CatalogImportProblem): readonly unknown[] => {
    if (problem.fields && problem.fields.length > 0) {
        return problem.fields;
    }
    const listed = problem.params?.fields;
    return Array.isArray(listed) ? listed : [];
};

/** The barcode a problem names in `params.barcode`, or `null` when it names none. */
const paramBarcode = (problem: CatalogImportProblem): string | null => {
    const barcode = problem.params?.barcode;
    return typeof barcode === 'string' ? barcode : null;
};

type ProblemGroup = {
    key: string;
    rows: number[];
    problem: CatalogImportProblem;
};

/** The row's group for a problem: its key and every row it names. */
const problemGroupOf = (
    code: BulkFixCode,
    line: CatalogImportLineResult,
    problem: CatalogImportProblem,
): { key: string; rows: number[] } => {
    const sorted = (rows: number[]): number[] =>
        [...new Set([...rows, line.rowNumber])].sort((a, b) => a - b);
    switch (code) {
        case 'BARCODE_LOOKS_LIKE_IMEI':
            return { key: `row:${line.rowNumber}`, rows: [line.rowNumber] };
        case 'GROUP_FIELDS_DIFFER': {
            // The preview's SKU group: new-product lines carry it.
            const rows = sorted(line.groupRows);
            return { key: line.groupKey ?? `rows:${rows.join(',')}`, rows };
        }
        case 'VARIANT_FIELDS_DIFFER':
        case 'BARCODE_SHARED_ACROSS_SKUS': {
            // New-variant and restock lines have no group key: `params.rows`.
            const rows = sorted(numberRows(problem.params?.rows));
            return { key: `rows:${rows.join(',')}`, rows };
        }
    }
};

/**
 * The groups the last check reported a kind on, once each: from the rows
 * that carry the problem (the user's excluded rows only with
 * `withExcluded`), in row order.
 */
const problemGroups = (
    source: BulkFixSource,
    code: BulkFixCode,
    withExcluded = false,
): ProblemGroup[] => {
    const groups = new Map<string, ProblemGroup>();
    for (const line of source.preview?.lines ?? []) {
        if (!withExcluded && source.exclusions.has(line.rowNumber)) {
            continue;
        }
        const problem = line.problems.find(
            (candidate) => candidate.code === code,
        );
        if (!problem) {
            continue;
        }
        const { key, rows } = problemGroupOf(code, line, problem);
        if (!groups.has(key)) {
            groups.set(key, { key, rows, problem });
        }
    }
    return [...groups.values()];
};

// ---- The defaults -----------------------------------------------------------

/** The IMEI move: the barcode goes to Lot number and Barcode is cleared. */
const imeiPatches = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    const named = paramBarcode(problem);
    for (const line of lines) {
        // An edit since the check (a lot number, another barcode) has
        // already answered the problem.
        if (blank(line.barcode) || !blank(line.lotNumber)) {
            continue;
        }
        if (named !== null && codeKey(named) !== codeKey(line.barcode)) {
            continue;
        }
        patches.set(line.rowNumber, {
            lotNumber: (line.barcode ?? '').trim(),
            barcode: null,
        });
    }
    return patches;
};

/** The figures that are amounts: compared in tetri (`domain.md`, Money). */
const AMOUNT_FIELDS: readonly VariantMismatchField[] = [
    'salePrice',
    'wholesalePrice',
];

/**
 * What a figure's cell is compared and ranked by: an amount in integer
 * tetri, a count as it is, a blank `null`.
 */
const figureKey = (
    field: VariantMismatchField,
    value: number | null | undefined,
): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    return AMOUNT_FIELDS.includes(field) ? tetriFromDecimal(value) : value;
};

/** The number a key writes to the line: an amount back in decimal GEL. */
const figureOfKey = (
    field: VariantMismatchField,
    key: number | null,
): number | null =>
    key === null || !AMOUNT_FIELDS.includes(field) ? key : gelNumberOf(key);

/**
 * The values of a figure with their rows: the most common number first,
 * the higher on a tie, and a blank cell last. Amounts are counted and
 * ranked in tetri; each value is that amount in GEL, as the patch writes it.
 */
const rankedValues = (
    lines: readonly ParsedLine[],
    field: VariantMismatchField,
): BulkFixValue[] => {
    const byKey = new Map<number | null, number[]>();
    for (const line of lines) {
        const key = figureKey(field, line[field]);
        byKey.set(key, [...(byKey.get(key) ?? []), line.rowNumber]);
    }
    return [...byKey.entries()]
        .sort(([aKey, aRows], [bKey, bRows]) => {
            if (aKey === null || bKey === null) {
                return aKey === null ? 1 : -1;
            }
            return bRows.length - aRows.length || bKey - aKey;
        })
        .map(([key, rows]) => ({ value: figureOfKey(field, key), rows }));
};

/**
 * One value per differing figure: the most common number, the higher on a
 * tie. A blank cell takes no part in the count and is given that value; a
 * line whose cell is unreadable takes no part and is not changed. A figure
 * no line has a number for is left as it is.
 */
const variantFix = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
): { patches: Map<number, ParsedLinePatch>; choices: BulkFixChoice[] } => {
    const listed = listedFields(problem);
    const fields = VARIANT_MISMATCH_FIELDS.filter((field) =>
        listed.includes(field),
    );
    const patches = new Map<number, ParsedLinePatch>();
    const choices: BulkFixChoice[] = [];
    for (const field of fields) {
        const readable = lines.filter(
            (line) => line.unreadable?.[field] === undefined,
        );
        const values = rankedValues(readable, field);
        const suggested = values[0]?.value ?? null;
        if (values.length === 0) {
            continue;
        }
        choices.push({ field, values, suggested });
        if (suggested === null) {
            continue;
        }
        // A line holding the suggested amount in another decimal (0.1 + 0.2
        // for 0.3) is written too: bos-api compares the numbers sent, so it
        // would still see two values.
        for (const line of readable) {
            if (line[field] !== suggested) {
                patches.set(line.rowNumber, {
                    ...patches.get(line.rowNumber),
                    [field]: suggested,
                });
            }
        }
    }
    return { patches, choices };
};

/**
 * The problems that say the check did not recognise a row's cell for a
 * product field: bos-api leaves such a row out when it compares the SKU's
 * rows, and out of `GROUP_FIELDS_DIFFER`'s fallback.
 */
const UNRECOGNISED_CODES = {
    name: ['NAME_REQUIRED'],
    category: ['NAME_TARGET_UNKNOWN', 'CATEGORY_AMBIGUOUS', 'NAME_MERGE_CYCLE'],
    brand: ['NAME_TARGET_UNKNOWN', 'BRAND_AMBIGUOUS', 'NAME_MERGE_CYCLE'],
    unit: ['UNIT_UNKNOWN'],
    vatType: ['VAT_TYPE_UNKNOWN'],
} as const satisfies Record<GroupFixField, readonly CatalogImportCode[]>;

const groupFieldsOf = (problem: CatalogImportProblem): GroupFixField[] => {
    const listed = listedFields(problem);
    return (Object.keys(GROUP_FIELD_CELLS) as GroupFixField[]).filter((field) =>
        listed.includes(field),
    );
};

/** Whether the row's cells for the field are filled in and the last check recognised them. */
const givesField = (
    line: ParsedLine,
    field: GroupFixField,
    result: CatalogImportLineResult | undefined,
): boolean => {
    const codes: readonly string[] = UNRECOGNISED_CODES[field];
    return (
        GROUP_FIELD_CELLS[field].some((cell) => !blank(line[cell])) &&
        !(result?.problems ?? []).some(
            (problem) =>
                codes.includes(problem.code) &&
                (problem.fields ?? []).includes(field),
        )
    );
};

/**
 * The default's row for each differing product field of the SKU: the first
 * by row number whose cells for it are filled in and recognised, as bos-api
 * takes it. A field no row gives is left out.
 */
const groupSources = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
    results: ReadonlyMap<number, CatalogImportLineResult>,
): Map<GroupFixField, ParsedLine> => {
    const sources = new Map<GroupFixField, ParsedLine>();
    for (const field of groupFieldsOf(problem)) {
        const source = lines.find((line) =>
            givesField(line, field, results.get(line.rowNumber)),
        );
        if (source) {
            sources.set(field, source);
        }
    }
    return sources;
};

/**
 * Each differing product field of the SKU from its row (`sources`: the
 * default's, or the drawer's pick for every field) to every other row.
 */
const groupPatches = (
    lines: readonly ParsedLine[],
    sources: ReadonlyMap<GroupFixField, ParsedLine>,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    for (const line of lines) {
        const patch: Record<string, string | null> = {};
        for (const [field, first] of sources) {
            if (line.rowNumber === first.rowNumber) {
                continue;
            }
            const cells = GROUP_FIELD_CELLS[field];
            if (
                cells.some(
                    (cell) => (line[cell] ?? null) !== (first[cell] ?? null),
                )
            ) {
                for (const cell of cells) {
                    patch[cell] = first[cell] ?? null;
                }
            }
        }
        if (Object.keys(patch).length > 0) {
            patches.set(line.rowNumber, patch as ParsedLinePatch);
        }
    }
    return patches;
};

/**
 * The first SKU by row keeps the shared barcode; the other SKUs' lines
 * clear it and fall back to their SKU.
 */
const sharedBarcodePatches = (
    lines: readonly ParsedLine[],
    problem: CatalogImportProblem,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    const [first] = lines;
    if (!first) {
        return patches;
    }
    const keeper = codeKey(first.sku);
    const named = paramBarcode(problem);
    for (const line of lines) {
        if (codeKey(line.sku) === keeper || blank(line.barcode)) {
            continue;
        }
        if (named !== null && codeKey(named) !== codeKey(line.barcode)) {
            continue;
        }
        patches.set(line.rowNumber, { barcode: null });
    }
    return patches;
};

/**
 * The group's rows the store holds and the user has not excluded (every
 * one with no `exclusions`), in row order.
 */
const groupLinesOf = (
    group: ProblemGroup,
    linesByRow: ReadonlyMap<number, ParsedLine>,
    exclusions: ReadonlySet<number> = new Set(),
): ParsedLine[] =>
    group.rows
        .filter((row) => !exclusions.has(row))
        .map((row) => linesByRow.get(row))
        .filter((line): line is ParsedLine => line !== undefined);

const resultsByRowOf = (
    source: BulkFixSource,
): Map<number, CatalogImportLineResult> =>
    new Map(
        (source.preview?.lines ?? []).map((result) => [
            result.rowNumber,
            result,
        ]),
    );

const groupOf = (
    code: BulkFixCode,
    group: ProblemGroup,
    linesByRow: ReadonlyMap<number, ParsedLine>,
    source: BulkFixSource,
): BulkFixGroup => {
    const lines = groupLinesOf(group, linesByRow, source.exclusions);
    const rows = lines.map((line) => line.rowNumber);
    switch (code) {
        case 'BARCODE_LOOKS_LIKE_IMEI':
            return {
                code,
                key: group.key,
                rows,
                patches: imeiPatches(lines, group.problem),
                choices: [],
            };
        case 'VARIANT_FIELDS_DIFFER': {
            const { patches, choices } = variantFix(lines, group.problem);
            return { code, key: group.key, rows, patches, choices };
        }
        case 'GROUP_FIELDS_DIFFER':
            return {
                code,
                key: group.key,
                rows,
                patches: groupPatches(
                    lines,
                    groupSources(lines, group.problem, resultsByRowOf(source)),
                ),
                choices: [],
            };
        case 'BARCODE_SHARED_ACROSS_SKUS':
            return {
                code,
                key: group.key,
                rows,
                patches: sharedBarcodePatches(lines, group.problem),
                choices: [],
            };
    }
};

const linesByRowOf = (lines: readonly ParsedLine[]): Map<number, ParsedLine> =>
    new Map(lines.map((line) => [line.rowNumber, line]));

/**
 * One kind's groups over the current lines' problems, each with its
 * default: the IMEI move (`BARCODE_LOOKS_LIKE_IMEI`, one group per row),
 * the most common figure per variant (`VARIANT_FIELDS_DIFFER`, keyed by
 * `params.rows`), per SKU each product field from the first row that
 * fills it in recognisably (`GROUP_FIELDS_DIFFER`, the preview's SKU
 * group) and the first SKU by row keeping a shared barcode
 * (`BARCODE_SHARED_ACROSS_SKUS`, keyed by `params.rows`). Rows the user
 * excluded take no part.
 */
export const bulkFixGroups = (
    source: BulkFixSource,
    code: BulkFixCode,
): BulkFixGroup[] => {
    const linesByRow = linesByRowOf(source.lines);
    return problemGroups(source, code).map((group) =>
        groupOf(code, group, linesByRow, source),
    );
};

/** The group of a kind a row belongs to (the drawer's), or `null`. */
export const bulkFixGroupOf = (
    source: BulkFixSource,
    code: BulkFixCode,
    rowNumber: number,
): BulkFixGroup | null =>
    bulkFixGroups(source, code).find((group) =>
        group.rows.includes(rowNumber),
    ) ?? null;

// ---- The drawer's pick ------------------------------------------------------

/**
 * What the row drawer picked for its row's group (bos-app#219, US-07): one
 * of a variant figure's values (`VARIANT_FIELDS_DIFFER`), the row of the
 * SKU whose product fields every row takes (`GROUP_FIELDS_DIFFER`), or the
 * kind's default for that group alone: the IMEI move
 * (`BARCODE_LOOKS_LIKE_IMEI`) or the shared barcode kept on the first SKU
 * by row (`BARCODE_SHARED_ACROSS_SKUS`).
 */
export type GroupPick =
    | { code: Extract<BulkFixCode, 'BARCODE_LOOKS_LIKE_IMEI'> }
    | { code: Extract<BulkFixCode, 'BARCODE_SHARED_ACROSS_SKUS'> }
    | {
          code: Extract<BulkFixCode, 'VARIANT_FIELDS_DIFFER'>;
          field: VariantMismatchField;
          /** One of the figure's values (`BulkFixChoice.values`); `null` is blank. */
          value: number | null;
      }
    | {
          code: Extract<BulkFixCode, 'GROUP_FIELDS_DIFFER'>;
          /** A row of the group: its cells for the differing fields. */
          sourceRow: number;
      };

/**
 * What the drawer's pick writes to the group of `rowNumber`: the picked
 * value of the figure to every row of the variant that carries another
 * (a line whose cell is unreadable takes no part, as for the default), or
 * the picked row's cells for the differing product fields to every other
 * row of the SKU, or the default of an IMEI or shared-barcode group (its
 * `patches`). Only the rows it changes; nothing for a value the figure
 * does not have, a row outside the group, or a row in no group of the kind.
 * Rows the user excluded take no part, except in the SKU's pick ("Use for
 * all rows"): it writes every row of the SKU, excluded ones included, so
 * the rows still agree when one is included again, and an excluded row
 * can be its source, as the drawer lists every row of the SKU.
 */
export const groupPickPatches = (
    source: BulkFixSource,
    rowNumber: number,
    pick: GroupPick,
): Map<number, ParsedLinePatch> => {
    const patches = new Map<number, ParsedLinePatch>();
    if (
        pick.code === 'BARCODE_LOOKS_LIKE_IMEI' ||
        pick.code === 'BARCODE_SHARED_ACROSS_SKUS'
    ) {
        return new Map(
            bulkFixGroupOf(source, pick.code, rowNumber)?.patches ?? patches,
        );
    }
    if (pick.code === 'VARIANT_FIELDS_DIFFER') {
        const choice = bulkFixGroupOf(
            source,
            pick.code,
            rowNumber,
        )?.choices.find((candidate) => candidate.field === pick.field);
        const pickedKey = figureKey(pick.field, pick.value);
        const isPicked = (entry: BulkFixValue): boolean =>
            figureKey(pick.field, entry.value) === pickedKey;
        if (!choice?.values.some(isPicked)) {
            return patches;
        }
        const linesByRow = linesByRowOf(source.lines);
        for (const entry of choice.values) {
            for (const row of entry.rows) {
                // The picked amount's own rows are written only where their
                // decimal reads otherwise (0.1 + 0.2 for 0.3), as bos-api
                // compares the numbers sent.
                if (
                    isPicked(entry) &&
                    linesByRow.get(row)?.[pick.field] === pick.value
                ) {
                    continue;
                }
                patches.set(row, { [pick.field]: pick.value });
            }
        }
        return patches;
    }
    const group = problemGroups(source, pick.code, true).find((candidate) =>
        candidate.rows.includes(rowNumber),
    );
    if (!group) {
        return patches;
    }
    const lines = groupLinesOf(group, linesByRowOf(source.lines));
    const keeper = lines.find((line) => line.rowNumber === pick.sourceRow);
    return keeper
        ? groupPatches(
              lines,
              new Map(
                  groupFieldsOf(group.problem).map((field) => [field, keeper]),
              ),
          )
        : patches;
};

/**
 * The row the drawer marks Suggested for the SKU of `rowNumber`
 * (`GROUP_FIELDS_DIFFER`): the one the default takes every differing field
 * from, so that taking it for all rows is the default. `null` when the
 * fields come from different rows, a field has no row to come from, or the
 * row is in no such group.
 */
export const groupFieldsSuggestedRow = (
    source: BulkFixSource,
    rowNumber: number,
): number | null => {
    const group = problemGroups(source, 'GROUP_FIELDS_DIFFER').find(
        (candidate) => candidate.rows.includes(rowNumber),
    );
    if (!group) {
        return null;
    }
    const lines = groupLinesOf(
        group,
        linesByRowOf(source.lines),
        source.exclusions,
    );
    const sources = groupSources(lines, group.problem, resultsByRowOf(source));
    const rows = new Set([...sources.values()].map((line) => line.rowNumber));
    return sources.size === groupFieldsOf(group.problem).length &&
        rows.size === 1
        ? [...rows][0]!
        : null;
};

/**
 * The groups that have a fix: those whose default changes a row. A group
 * it would change none of has none, such as a variant whose figures differ
 * only by an unreadable cell (the default skips it, yet bos-api still sees
 * two values): its rows stay flagged for the owner to correct in the drawer.
 */
export const groupsWithFix = (
    groups: readonly BulkFixGroup[],
): BulkFixGroup[] => groups.filter((group) => group.patches.size > 0);

/** How many rows the groups' defaults change. */
export const changedRowCount = (groups: readonly BulkFixGroup[]): number =>
    new Set(groups.flatMap((group) => [...group.patches.keys()])).size;

/** The groups' patches as one, by row. */
export const mergedPatches = (
    groups: readonly BulkFixGroup[],
): Map<number, ParsedLinePatch> => {
    const merged = new Map<number, ParsedLinePatch>();
    for (const group of groups) {
        for (const [row, patch] of group.patches) {
            merged.set(row, { ...merged.get(row), ...patch });
        }
    }
    return merged;
};

/** The lines with the patches applied; a line no patch names is kept as it is. */
export const withPatches = (
    lines: ParsedLine[],
    patches: ReadonlyMap<number, ParsedLinePatch>,
): ParsedLine[] =>
    patches.size === 0
        ? lines
        : lines.map((line) => {
              const patch = patches.get(line.rowNumber);
              return patch ? { ...line, ...patch } : line;
          });

/**
 * What "Import with suggested fixes" changes: the lines, the rows per fix
 * (`counts`) and the groups per fix it changes a row of (`groups`: IMEIs,
 * variants, SKUs, shared barcodes; its summary's counts, bos-app#221).
 */
export type AllFixesPlan = {
    lines: ParsedLine[];
    changedRows: number[];
    counts: Record<BulkFixCode, number>;
    groups: Record<BulkFixCode, number>;
};

/**
 * Every default, kind after kind in `BULK_FIX_CODES` order, each on the
 * lines the one before left: the plan "Import with suggested fixes"
 * applies as one bulk action.
 */
export const allFixesPlan = (source: BulkFixSource): AllFixesPlan => {
    let lines = source.lines;
    const changed = new Set<number>();
    const counts = {} as Record<BulkFixCode, number>;
    const changedGroups = {} as Record<BulkFixCode, number>;
    for (const code of BULK_FIX_CODES) {
        const groups = bulkFixGroups({ ...source, lines }, code);
        const patches = mergedPatches(groups);
        counts[code] = patches.size;
        changedGroups[code] = groupsWithFix(groups).length;
        patches.forEach((_, row) => changed.add(row));
        lines = withPatches(lines, patches);
    }
    return {
        lines,
        changedRows: [...changed].sort((a, b) => a - b),
        counts,
        groups: changedGroups,
    };
};

/**
 * Two plans applied one after the other as one step: the second's lines,
 * the rows either changed, and each kind's rows and groups added up (the
 * second answers problems the first revealed, so its groups are others).
 */
export const joinedPlans = (
    first: AllFixesPlan,
    next: AllFixesPlan,
): AllFixesPlan => {
    const counts = {} as Record<BulkFixCode, number>;
    const groups = {} as Record<BulkFixCode, number>;
    for (const code of BULK_FIX_CODES) {
        counts[code] = first.counts[code] + next.counts[code];
        groups[code] = first.groups[code] + next.groups[code];
    }
    return {
        lines: next.lines,
        changedRows: [
            ...new Set([...first.changedRows, ...next.changedRows]),
        ].sort((a, b) => a - b),
        counts,
        groups,
    };
};

// ---- Make main --------------------------------------------------------------

const BARCODES_KEPT_AS_ALTERNATES =
    'BARCODES_KEPT_AS_ALTERNATES' satisfies CatalogImportCode;

/**
 * Make main: `primaryBarcode` set on the chosen line and cleared on the
 * variant's other lines, the rows the line's `BARCODES_KEPT_AS_ALTERNATES`
 * names (`params.rows`; the line alone without that warning). Only the rows
 * it changes.
 */
export const makeMainPatches = (
    source: Pick<CatalogImportState, 'lines' | 'preview'>,
    rowNumber: number,
): Map<number, ParsedLinePatch> => {
    const warning = source.preview?.lines
        .find((line) => line.rowNumber === rowNumber)
        ?.warnings.find(
            (candidate) => candidate.code === BARCODES_KEPT_AS_ALTERNATES,
        );
    const rows = new Set([...numberRows(warning?.params?.rows), rowNumber]);
    const patches = new Map<number, ParsedLinePatch>();
    for (const line of source.lines) {
        if (!rows.has(line.rowNumber)) {
            continue;
        }
        if (line.rowNumber === rowNumber) {
            if (line.primaryBarcode !== true) {
                patches.set(line.rowNumber, { primaryBarcode: true });
            }
        } else if (line.primaryBarcode !== undefined) {
            patches.set(line.rowNumber, { primaryBarcode: undefined });
        }
    }
    return patches;
};
